import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import MeshGradient from '../../components/ui/MeshGradient';
import NoiseTexture from '../../components/ui/NoiseTexture';
import './Auth.css';

const Verify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { clientId } = window.CONFIG.cognito;

      const response = await fetch(`https://cognito-idp.eu-west-2.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp',
        },
        body: JSON.stringify({
          ClientId: clientId,
          Username: email,
          ConfirmationCode: code,
        }),
      });

      const data = await response.json();

      if (!data.__type) {
        navigate('/auth/login', { state: { verified: true } });
      } else if (data.__type?.includes('CodeMismatchException')) {
        setError('Invalid verification code');
      } else if (data.__type?.includes('ExpiredCodeException')) {
        setError('Code has expired. Please request a new one.');
      } else {
        setError(data.message || 'Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      const { clientId } = window.CONFIG.cognito;

      const response = await fetch(`https://cognito-idp.eu-west-2.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ResendConfirmationCode',
        },
        body: JSON.stringify({
          ClientId: clientId,
          Username: email,
        }),
      });

      const data = await response.json();

      if (!data.__type) {
        setResent(true);
        setTimeout(() => setResent(false), 3000);
      } else {
        setError('Failed to resend code. Please try again.');
      }
    } catch (err) {
      setError('Failed to resend code.');
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="auth-container">
        <MeshGradient />
        <NoiseTexture />
        <div className="auth-card">
          <div className="auth-header">
            <img 
              src={`${process.env.PUBLIC_URL}/images/fokusLogo.svg`} 
              alt="Fokus Logo" 
              className="auth-logo-image"
            />
            <h1 className="auth-title">Verify your email</h1>
          </div>
          <p className="auth-message">
            No email provided. Please <Link to="/auth/register" className="auth-link">sign up</Link> first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <MeshGradient />
      <NoiseTexture />
      <div className="auth-card">
        <div className="auth-header">
          <img 
            src={`${process.env.PUBLIC_URL}/images/fokusLogo.svg`} 
            alt="Fokus Logo" 
            className="auth-logo-image"
          />
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-tagline">We sent a verification code to</p>
        </div>

        <div className="auth-email-display">
          <span>{email}</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" style={{ marginTop: '1rem' }}>
          {error && <div className="auth-error">{error}</div>}
          {resent && <div className="auth-success">New code sent!</div>}
          
          <div className="auth-field">
            <label htmlFor="code">Verification code</label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
              autoFocus
              maxLength={6}
              className="auth-code-input"
            />
          </div>

          <button type="submit" className="auth-button auth-button--primary" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify email'}
          </button>
        </form>

        <div className="auth-footer">
          <div className="auth-footer-links">
            <span>Didn't receive a code?</span>
            <button 
              onClick={handleResend} 
              className="auth-link-button"
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Resend'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verify;
